import { Component } from '@theme/component';

/**
 * Maps vertical page scroll to horizontal gallery movement on horizontal PDP (desktop).
 *
 * @typedef {Object} HorizontalPdpScrollRefs
 * @property {HTMLElement} track - Horizontal flex track of gallery images
 * @property {HTMLElement} spacer - Scroll distance spacer below the pinned viewport
 * @property {HTMLElement} gallery - 70vh gallery viewport
 */

/**
 * @extends {Component<HorizontalPdpScrollRefs>}
 */
class HorizontalPdpScroll extends Component {
  requiredRefs = ['track', 'spacer', 'gallery'];

  /** @type {MediaQueryList} */
  #desktopMediaQuery = window.matchMedia('(min-width: 750px)');

  /** @type {MediaQueryList} */
  #reducedMotionMediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

  /** @type {AbortController} */
  #abortController = new AbortController();

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.#desktopMediaQuery.addEventListener('change', this.#handleLayoutChange, { signal });
    this.#reducedMotionMediaQuery.addEventListener('change', this.#handleLayoutChange, { signal });
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    window.addEventListener('resize', this.#handleLayoutChange, { signal });

    this.#setupLayout();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
    this.#resizeObserver?.disconnect();
  }

  #setupLayout = () => {
    this.#resizeObserver?.disconnect();

    if (!this.#desktopMediaQuery.matches || this.#reducedMotionMediaQuery.matches) {
      this.#resetScrollMapping();
      return;
    }

    const { track, spacer, gallery } = this.refs;

    gallery.style.overflowX = 'hidden';

    const updateSpacer = () => {
      const maxScroll = Math.max(0, track.scrollWidth - gallery.clientWidth);
      spacer.style.height = `${maxScroll}px`;
      this.#handleScroll();
    };

    this.#resizeObserver = new ResizeObserver(updateSpacer);
    this.#resizeObserver.observe(track);
    this.#resizeObserver.observe(gallery);
    updateSpacer();
  };

  #handleScroll = () => {
    if (!this.#desktopMediaQuery.matches || this.#reducedMotionMediaQuery.matches) {
      return;
    }

    const section = this.closest('.product-information--horizontal-scroll');
    const { track, gallery } = this.refs;

    if (!section) {
      return;
    }

    const maxScroll = Math.max(0, track.scrollWidth - gallery.clientWidth);
    const sectionTop = section.getBoundingClientRect().top + window.scrollY;
    const progress = Math.min(Math.max(window.scrollY - sectionTop, 0), maxScroll);

    track.style.transform = `translate3d(-${progress}px, 0, 0)`;
  };

  #handleLayoutChange = () => {
    this.#setupLayout();
  };

  #resetScrollMapping = () => {
    const { track, spacer, gallery } = this.refs;

    track.style.transform = '';
    spacer.style.height = '0px';
    gallery.style.overflowX = '';
  };
}

if (!customElements.get('horizontal-pdp-scroll')) {
  customElements.define('horizontal-pdp-scroll', HorizontalPdpScroll);
}
